import {
  Box,
  Container,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import EnterDate from "features/general/EnterDate";
import { GENERAL_FEED_TYPE, GENERAL_FEED_UNITS } from "model";
import { Dispatch, SetStateAction } from "react";

const cardSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  p: 2.5,
};

interface FeedPurchaseSingleStepProps {
  date: Date | null;
  setDate: Dispatch<SetStateAction<Date | null>>;
  dateError: boolean;
  feedType?: GENERAL_FEED_TYPE;
  setFeedType: Dispatch<SetStateAction<GENERAL_FEED_TYPE | undefined>>;
  feedTypeError: boolean;
  setFeedTypeError: Dispatch<SetStateAction<boolean>>;
  feedUnit?: GENERAL_FEED_UNITS;
  setFeedUnit: Dispatch<SetStateAction<GENERAL_FEED_UNITS | undefined>>;
  feedUnitError: boolean;
  setFeedUnitError: Dispatch<SetStateAction<boolean>>;
  feedAmount: string | undefined;
  setFeedAmount: Dispatch<SetStateAction<string | undefined>>;
  feedAmountError: boolean;
  setFeedAmountError: Dispatch<SetStateAction<boolean>>;
  cost: string | undefined;
  setCost: Dispatch<SetStateAction<string | undefined>>;
  costError: boolean;
  setCostError: Dispatch<SetStateAction<boolean>>;
  note: string | undefined;
  setNotes: Dispatch<SetStateAction<string | undefined>>;
}

export function FeedPurchaseSingleStep({
  date,
  setDate,
  dateError,
  feedType,
  setFeedType,
  feedTypeError,
  setFeedTypeError,
  feedUnit,
  setFeedUnit,
  feedUnitError,
  setFeedUnitError,
  feedAmount,
  setFeedAmount,
  feedAmountError,
  setFeedAmountError,
  cost,
  setCost,
  costError,
  setCostError,
  note,
  setNotes,
}: FeedPurchaseSingleStepProps) {
  const { t } = useTranslation();

  return (
    <Container style={{ textAlign: "left" }}>
      <Grid container spacing={2.5}>
        {/* Date */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t("feedPurchase.date.heading")}
          </Typography>
          <Box sx={cardSx}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <EnterDate
                  disabled={false}
                  date={date}
                  setDate={setDate}
                  dateError={dateError}
                  name={"feedPurchaseDate"}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Feed Type & Unit */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t("feedPurchase.typeUnit.heading")}
          </Typography>
          <Box sx={cardSx}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  required
                  value={feedType ?? ""}
                  onChange={(e) => {
                    const value = e.target.value as GENERAL_FEED_TYPE;
                    setFeedType(value);
                    if (value) setFeedTypeError(false);
                    else setFeedTypeError(true);
                  }}
                  error={feedTypeError}
                  label={t("feedPurchaseEvent.feedType.text")}
                  name="feedPurchaseEventFeedType"
                >
                  {Object.entries(GENERAL_FEED_TYPE).map(([key, value]) => (
                    <MenuItem value={value} key={key}>
                      {t(`feedPurchase.feedType.option.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  required
                  value={feedUnit ?? ""}
                  onChange={(e) => {
                    const value = e.target.value as GENERAL_FEED_UNITS;
                    setFeedUnit(value);
                    if (value) setFeedUnitError(false);
                    else setFeedUnitError(true);
                  }}
                  error={feedUnitError}
                  label={t("feedPurchaseEvent.feedUnit.text")}
                  name="feedPurchaseEvent.feedUnit"
                >
                  {Object.entries(GENERAL_FEED_UNITS).map(([key, value]) => (
                    <MenuItem value={value} key={key}>
                      {t(`feedPurchase.feedUnit.option.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Amount & Cost */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t("feedPurchase.amountCost.heading")}
          </Typography>
          <Box sx={cardSx}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  value={feedAmount}
                  type="number"
                  id="feedAmount"
                  error={feedAmountError}
                  onChange={(event) => {
                    if (event.target.value === "") {
                      setFeedAmountError(false);
                      setFeedAmount(undefined);
                    } else {
                      setFeedAmount(event.target.value);
                      const value = Number(event.target.value);
                      setFeedAmountError(Number.isNaN(value) || value <= 0);
                    }
                  }}
                  label={t("feedPurchaseEvent.feedAmount.label")}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  value={cost}
                  error={costError}
                  type="number"
                  id="feedPurchaseCost"
                  onChange={(event) => {
                    if (event.target.value === "") {
                      setCostError(false);
                      setCost(undefined);
                    } else {
                      setCost(event.target.value);
                      const value = Number(event.target.value);
                      setCostError(Number.isNaN(value) || value < 0);
                    }
                  }}
                  label={t("feedPurchaseEvent.cost.label")}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Additional details (optional) */}
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {t("common.additionalDetails.heading")}
          </Typography>
          <Box sx={cardSx}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  value={note ?? ""}
                  id="feedPurchaseNotes"
                  fullWidth
                  onChange={(event) => {
                    setNotes(event.target.value);
                  }}
                  label={t("reseedEvent.notes.label")}
                  multiline
                  maxRows={12}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
